/**
 * _walkthrough_fill.js: the first-run walkthrough types a sample quote into
 * the real quote form while the person watches (step 2, `fill_quote`).
 *
 * walkthrough_controller.js calls fillQuoteForm once the form is on screen.
 * Each field is shown first (the controller scrolls it clear of the card and
 * points the quiet spotlight at it), then typed through _field_write.js, so
 * every watcher of the form reacts as it does to a person: `input` per
 * character, one `change` per field. Nothing is focused, so no keyboard
 * comes up, and nothing is submitted: the person taps Create Quote.
 *
 * The order: switch to a new client (only when Existing Client is chosen),
 * the two names lit together, the phone, the email, the address (typed, then
 * the sample's own three suggestions offered and the first picked, so no
 * lookup leaves the browser), the description once its editor is built, and,
 * where the form has a price, the pricing: the line items' toggle pointed at
 * with its note, and the sample's two line items typed into real rows (the
 * toggle opens the first, Add Item the second). The flat Price field is
 * passed over and never typed: line items lock it to their total. The sample comes from
 * application/services/walkthrough_sample.py (for_region); the timings from
 * onboardTiming() in _onboard.js, whose keystroke is 0 under reduced motion,
 * so each value is then written whole.
 *
 * Markup: templates/components/forms/quote_form.html,
 * components/forms/_client_site_fields.html and macros/create_line_items.html
 * (rows drawn by static/js/utils/line-items-manager.js).
 */
import { check, typeText, wait } from "./_field_write.js";

// Tiptap mounts lazily; past this the words go into the editor's textarea.
const EDITOR_WAIT_MS = 1500;

const FIELD = {
    existing: "#quote_existing_client",
    newClient: "#quote_new_client",
    firstName: "#quote_first_name",
    lastName: "#quote_last_name",
    phone: "#quote_phone",
    email: "#quote_email",
    address: "#quote_address_input",
    description: "#description-field",
    amount: "#quoteAmount",
    lineItems: '[data-controller~="create-line-items"]',
    submit: 'button[type="submit"]',
};

// Inside the line items section and each of its rows.
const ITEMS = {
    toggle: ".create-section-toggle",
    add: '[data-action~="create-line-items#addItem"]',
    row: ".line-item-card",
    description: '[data-line-item-field="description"]',
    quantity: 'input[aria-label="Quantity"]',
    unit: 'select[aria-label="Unit"]',
    price: "[data-line-item-price]",
};

// The card state the controller shows while the line items are pointed at.
const ITEMS_NOTE = "fill_quote_items";
// Of a read, how long the arrow rests on the line items' toggle before the
// rows open: the note's words go on being read while the rows type.
const ITEMS_LEAD = 0.5;

/**
 * Type `sample` into the quote `form`. Resolves with the form's submit
 * button once every field is written; rejects with an AbortError the moment
 * `signal` aborts, leaving no timer running.
 *
 * @param {object} args
 * @param {HTMLFormElement} args.form    the #quoteForm element
 * @param {object} args.sample           walkthrough_sample.for_region()
 * @param {object} args.timing           {reduced, keystroke, hold, pick, spotlight, read} in ms
 * @param {Function} args.show           async (targets, {scrollTo, point}) => void, from the controller; `point` draws the arrow
 * @param {Function} args.note           async (stateKey) => void: the controller swaps the card
 * @param {AbortSignal} args.signal
 * @returns {Promise<HTMLButtonElement>}
 */
export async function fillQuoteForm({ form, sample, timing, show, note, signal }) {
    const run = { form, timing, show, note, signal };
    await chooseNewClient(run);
    await fillNames(run, sample);
    await fillPhone(run, sample);
    await fillEmail(run, sample);
    await fillAddress(run, sample);
    await fillDescription(run, sample);
    await fillPricing(run, sample);
    return need(form, FIELD.submit);
}

// The sample is a new client. Choosing New also points the form at
// /add_quote_with_client (client_mode_controller.js); the frame lets the
// new-client fields lay out before the first of them is measured.
async function chooseNewClient(run) {
    if (!need(run.form, FIELD.existing).checked) return;
    check(need(run.form, FIELD.newClient));
    await hold(run);
    await nextFrame(run.signal);
}

async function fillNames(run, sample) {
    const first = need(run.form, FIELD.firstName);
    const last = need(run.form, FIELD.lastName);
    await light(run, [first, last]);
    await type(run, first, sample.first_name);
    await type(run, last, sample.last_name);
    await hold(run);
}

async function fillPhone(run, sample) {
    const phone = need(run.form, FIELD.phone);
    await light(run, phone);
    await type(run, phone, sample.phone);
    await hold(run);
}

async function fillEmail(run, sample) {
    const email = need(run.form, FIELD.email);
    await light(run, email);
    await type(run, email, sample.email);
    await hold(run);
}

/**
 * Drop `suggestions` down under the field through its own autocomplete and
 * mark the first as the one about to be picked (onboarding.css). Also how the
 * /dev/cards gallery draws step 2a, so it shows this list, not a copy.
 */
export function offerPick(autocomplete, suggestions) {
    autocomplete.offer(suggestions);
    autocomplete.dropdown.firstElementChild?.setAttribute("data-onboard-pick", "");
}

// offer() is not awaited and its answer never stops the pick: a list that
// closed under a tap is the controller's tap verdict to judge, and the form
// still gets the sample's address parts.
async function fillAddress(run, sample) {
    const input = need(run.form, FIELD.address);
    const autocomplete = input.autocompleteInstance;
    if (!autocomplete) throw new Error(`walkthrough fill: ${FIELD.address} has no address autocomplete`);
    await light(run, input);
    await type(run, input, sample.street);
    offerPick(autocomplete, sample.suggestions);
    await light(run, [input, autocomplete.dropdown], { scrollTo: input });
    await hold(run, run.timing.pick);
    autocomplete.select(sample.suggestions[0]);
    await hold(run);
}

// The editor mounts only once it is on screen, so it is shown before it is
// waited for. Without a built editor the words go into its textarea, which
// the editor loads from when it does mount.
async function fillDescription(run, sample) {
    const field = need(run.form, FIELD.description);
    await light(run, field);
    if (await editorReady(field, run.signal)) await type(run, field, sample.description);
    else await typeText(need(field, "textarea"), sample.description, { signal: run.signal });
    await hold(run);
}

// Only a person who sees money has the price field and the line items
// (view_financials). The line items' toggle gets its note and the drawn
// arrow (not the fill's quiet one), so the eye is pulled to the card; the
// note goes up before the toggle is pointed at, so the card and the control
// it talks about arrive together. The rows are then typed.
async function fillPricing(run, sample) {
    if (!run.form.querySelector(FIELD.amount)) return;
    const section = need(run.form, FIELD.lineItems);
    await unlessStopped(run.note(ITEMS_NOTE), run.signal);
    await light(run, need(section, ITEMS.toggle), { point: true });
    await hold(run, run.timing.read * ITEMS_LEAD);
    for (const [index, item] of sample.line_items.entries()) {
        need(section, index === 0 ? ITEMS.toggle : ITEMS.add).click();
        await fillLineItem(run, [...section.querySelectorAll(ITEMS.row)].at(-1), item);
    }
}

// The manager reads the row's own inline handlers: input for quantity and
// price, change for the description and the unit.
async function fillLineItem(run, row, item) {
    if (!row) throw new Error(`walkthrough fill: no ${ITEMS.row} after opening a line item`);
    await light(run, row);
    await type(run, need(row, ITEMS.description), item.description);
    await type(run, need(row, ITEMS.quantity), item.quantity);
    choose(need(row, ITEMS.unit), item.unit);
    await type(run, need(row, ITEMS.price), item.unit_price);
    await hold(run);
}

function choose(select, value) {
    select.value = value;
    select.dispatchEvent(new Event("change", { bubbles: true }));
}

// --- Pacing ----------------------------------------------------------------

// The controller scrolls and points; the cutout then glides for `spotlight` ms.
async function light(run, targets, options = {}) {
    await unlessStopped(run.show(targets, options), run.signal);
    await wait(run.timing.spotlight, run.signal);
}

function type(run, el, text) {
    return typeText(el, text, { keystroke: run.timing.keystroke, signal: run.signal });
}

function hold(run, ms = run.timing.hold) {
    return wait(ms, run.signal);
}

// True once the editor is built. A page whose editor offers no promise, or
// whose promise settles false or past the cap, gets false.
async function editorReady(field, signal) {
    if (typeof field.chippieWhenReady !== "function") return false;
    let timer;
    const cap = new Promise((resolve) => { timer = setTimeout(() => resolve(false), EDITOR_WAIT_MS); });
    const built = Promise.resolve(field.chippieWhenReady()).then((ready) => ready !== false, () => false);
    try {
        return await unlessStopped(Promise.race([built, cap]), signal);
    } finally {
        clearTimeout(timer);
    }
}

function nextFrame(signal) {
    signal.throwIfAborted();
    return new Promise((resolve, reject) => {
        const stop = () => {
            cancelAnimationFrame(frame);
            reject(signal.reason);
        };
        const frame = requestAnimationFrame(() => {
            signal.removeEventListener("abort", stop);
            resolve();
        });
        signal.addEventListener("abort", stop, { once: true });
    });
}

// `promise`'s outcome, or the stop's reason the moment `signal` aborts.
function unlessStopped(promise, signal) {
    signal.throwIfAborted();
    return new Promise((resolve, reject) => {
        const stop = () => reject(signal.reason);
        signal.addEventListener("abort", stop, { once: true });
        Promise.resolve(promise).then(resolve, reject)
            .finally(() => signal.removeEventListener("abort", stop));
    });
}

function need(root, selector) {
    const el = root.querySelector(selector);
    if (!el) throw new Error(`walkthrough fill: no ${selector} in the quote form`);
    return el;
}
